import type { Address } from 'viem';
import { sleep } from '@/modules/sleep';

// A pending tx: what the consumer's storage persists per address and what's
// held in memory during polling. Only this serialized shape survives a
// reload — callbacks the consumer keeps outside it (keyed by hash or
// otherwise) are lost, so onCompleted must carry every side effect that has
// to run for reloaded txs too.
export type PendingTx<E extends string, D> = {
  hash: string;
  networkIdHex: string;
  // Opaque consumer payload — passed through to onCompleted, never read here.
  details: D;
  // Event name + props emitted once through onTrack (with the final `status`
  // merged in) when the receipt lands.
  event: E;
  trackProps: Record<string, unknown>;
  // Set (and persisted) once the mined/reverted event was sent, so a reload
  // during the post-receipt cascade doesn't re-emit it. Optional — entries
  // written before this field existed simply track once more.
  tracked?: boolean;
  // Epoch ms of submit(). Optional — entries written before this field
  // existed get stamped at load time. Bounds the receipt poll: a hash the
  // chain keeps failing to resolve (sped-up/cancelled replacement, mempool
  // eviction) is dropped once older than TX_EXPIRY_MS, instead of wedging
  // getPending() consumers on every page load forever.
  submittedAt?: number;
};

// The in-memory counterpart of PendingTx: submit() and the setAddress() load
// both stamp submittedAt, so polling reads it unguarded — only the persisted
// shape keeps the field optional.
type StampedTx<E extends string, D> = PendingTx<E, D> & {
  submittedAt: number;
};

// Expiry only applies on FAILED receipt attempts — a mined tx returns its
// receipt on the first attempt regardless of age, and a known-but-pending tx
// waits inside waitForTransactionReceipt without throwing. So this is the
// "hash unknown / transport down for an hour+" terminal path, not a cap on
// slow-mining txs.
export const TX_EXPIRY_MS = 60 * 60_000;

// The tracker itself reads only `status`; the receipt is otherwise passed
// through to onCompleted untouched. Keeping the shape minimal lets every
// chain's client (zkSync ships its own receipt type) satisfy it
// structurally — no cast at the consumer boundary.
export type MinedReceipt = { status: 'success' | 'reverted' };

export type CompletedContext<E extends string, D> = {
  tx: PendingTx<E, D>;
  status: 'mined' | 'reverted';
  receipt: MinedReceipt;
};

type MinimalPublicClient = {
  waitForTransactionReceipt: (args: {
    hash: `0x${string}`;
  }) => Promise<MinedReceipt>;
};

type TxTrackerDeps<E extends string, D> = {
  getPublicClient: (networkIdHex: string) => MinimalPublicClient;
  storage: {
    get: (address: Address) => PendingTx<E, D>[];
    set: (address: Address, txs: PendingTx<E, D>[]) => void;
  };
  onCompleted: (ctx: CompletedContext<E, D>) => Promise<void> | void;
  onTrack: (event: E, props: Record<string, unknown>) => void;
  retryDelayMs?: number;
  now?: () => number;
};

type TxTracker<E extends string, D> = {
  setAddress: (address: Address | null) => void;
  submit: (tx: PendingTx<E, D>) => void;
  getPending: () => readonly PendingTx<E, D>[];
  subscribe: (
    listener: (pending: readonly PendingTx<E, D>[]) => void,
  ) => () => void;
};

export function createTxTracker<E extends string, D>(
  deps: TxTrackerDeps<E, D>,
): TxTracker<E, D> {
  let address: Address | null = null;
  let pending: StampedTx<E, D>[] = [];
  const listeners = new Set<(pending: readonly PendingTx<E, D>[]) => void>();
  // hashes currently being polled in this tracker instance. Prevents a
  // second poll loop for the same hash — e.g. a loaded list carrying
  // duplicate entries (double-submit and reload overlap are already caught
  // by the pending dedupe and the session abort).
  const polling = new Set<string>();
  // Abort flag per address session. Flipped on setAddress() so any in-flight
  // waitForTransactionReceipt() loops bail out instead of mutating state for
  // the old address.
  let session = 0;

  const retryDelayMs = deps.retryDelayMs ?? 3000;
  const now = deps.now ?? Date.now;

  function notify() {
    for (const listener of listeners) listener(pending);
  }

  function persist() {
    // Stryker disable next-line ConditionalExpression: every persist() call site runs behind the session/address guards, so address is always set here — the check is defense for future call sites only.
    if (address !== null) deps.storage.set(address, pending);
  }

  function removeByHash(hash: string) {
    pending = pending.filter((t) => t.hash !== hash);
    polling.delete(hash);
    persist();
    notify();
  }

  function pollOne(tx: StampedTx<E, D>, sessionAtStart: number) {
    if (polling.has(tx.hash)) return;
    polling.add(tx.hash);
    const client = deps.getPublicClient(tx.networkIdHex);

    const run = async () => {
      while (true) {
        if (session !== sessionAtStart) return;
        try {
          const receipt = await client.waitForTransactionReceipt({
            hash: tx.hash as `0x${string}`,
          });
          if (session !== sessionAtStart) return;
          const status: 'mined' | 'reverted' =
            receipt.status === 'success' ? 'mined' : 'reverted';
          await fireCompletion(tx, status, receipt, sessionAtStart);
          return;
        } catch {
          if (session !== sessionAtStart) return;
          // Terminal path for a hash that keeps failing to resolve: past
          // the expiry the entry is dropped from pending AND storage — no
          // mined/reverted event, no cascade; the tx never happened as far
          // as the chain is concerned. Checked only after a failure so a
          // late-but-mined receipt (laptop reopened hours later) still wins.
          if (now() - tx.submittedAt > TX_EXPIRY_MS) {
            removeByHash(tx.hash);
            return;
          }
          await sleep(retryDelayMs);
        }
      }
    };
    void run();
  }

  // `pending` only drops AFTER the whole onCompleted cascade resolves —
  // consumers keying in-flight UI off getPending() keep their loading state
  // until every completion side effect (not just the receipt) is done, so
  // there's no stale pre-tx flash between "mined" and "data refreshed".
  async function fireCompletion(
    tx: StampedTx<E, D>,
    status: 'mined' | 'reverted',
    receipt: MinedReceipt,
    sessionAtStart: number,
  ) {
    if (tx.tracked !== true) {
      deps.onTrack(tx.event, { ...tx.trackProps, status });
      tx.tracked = true;
      persist();
    }
    try {
      await deps.onCompleted({ tx, status, receipt });
    } catch {
      // onCompleted owns its own error reporting — a rejection here only
      // means the pending entry must still drop so consumers don't spin
      // forever.
    }
    if (session !== sessionAtStart) return;
    removeByHash(tx.hash);
  }

  function setAddress(next: Address | null) {
    // Stryker disable next-line UpdateOperator: loops compare the captured session for inequality only — any change of value aborts them, so the direction is irrelevant.
    session++;
    polling.clear();
    address = next;
    if (next === null) {
      pending = [];
      notify();
      return;
    }
    // Backfill submittedAt on entries written before the field existed —
    // they get a fresh expiry window from this load. Persist the stamp so
    // the window doesn't restart on every reload.
    const loaded = deps.storage.get(next);
    const backfilled = loaded.some((tx) => tx.submittedAt == null);
    pending = loaded.map((tx) => ({
      ...tx,
      submittedAt: tx.submittedAt ?? now(),
    }));
    if (backfilled) persist();
    notify();
    const currentSession = session;
    for (const tx of pending) pollOne(tx, currentSession);
  }

  function submit(tx: PendingTx<E, D>) {
    if (address === null)
      throw new Error('txTracker: submit() before setAddress()');
    if (pending.some((t) => t.hash === tx.hash)) return;
    const stamped = { ...tx, submittedAt: tx.submittedAt ?? now() };
    pending = [...pending, stamped];
    persist();
    notify();
    pollOne(stamped, session);
  }

  function subscribe(listener: (pending: readonly PendingTx<E, D>[]) => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  return {
    setAddress,
    submit,
    getPending: () => pending,
    subscribe,
  };
}
